import { useContext, useEffect } from 'react';
import { useIntl } from 'react-intl';

import { adoptOrphans, Button, Loader } from '@calm-web/design-system';

import guestPassImage from '@/../public/_n/images/guest-pass/asset-guest-pass.png';
import background from '@/../public/_n/images/guest-pass/background-guest-pass.jpg';
import { MAIN_CONTENT_ID } from '@/components/accessibility/SkipToMain';
import FullscreenBackground from '@/components/image/FullscreenBackground';
import Image from '@/components/image/NextImage';
import { useAuthPrompt } from '@/context/auth/AuthPromptProvider';
import { AuthModalContext } from '@/context/AuthModalContextProvider';
import { useAnalytics } from '@/hooks/analytics';
import { useUserState } from '@/hooks/store';
import { useComponentDidMount } from '@/hooks/utils/useComponentDidMount';
import { deviceSupportsAppStoreRedirect } from '@/utils/device';
import redirect from '@/utils/legacy/redirect';

import messages from '../messages/guestPassMessages';
import {
	ClaimYourPassText,
	Container,
	GuestPassImage,
	GuestPassImageColumn,
	GuestPassText,
	GuestPassTitle,
	Link,
	Overlay,
	RedeemColumn,
	RedeemItemsWrapper,
	TermsText,
	Wrapper,
} from './styles';

const appStoreURL = process.env.NEXT_PUBLIC_OFFER_CODE_REDEMPTION_URL;

interface GuestPassRedeemProps {
	canRedeem: boolean;
	code: string | undefined;
	error: Error | null;
	sender: string;
	trialLength: number;
	isLoading: boolean;
}

const GuestPassRedeem = ({
	canRedeem,
	code,
	error,
	sender,
	trialLength,
	isLoading,
}: GuestPassRedeemProps) => {
	const { formatMessage } = useIntl();
	const user = useUserState();
	const { logEvent } = useAnalytics();
	const { setAuthPrompt } = useAuthPrompt();
	const { setIsModalOpen } = useContext(AuthModalContext);

	useComponentDidMount(() => {
		const supportsAppStoreRedirect = deviceSupportsAppStoreRedirect(window?.navigator?.userAgent);
		const isOfferCode = code && code.length === 18;
		const redeemUrl =
			supportsAppStoreRedirect && isOfferCode
				? `${appStoreURL}${code}` /// https://apps.apple.com/redeem?ctx=offercodes&id=571800810&code=${code}
				: `/guest-pass/subscribe?guestPass=${code}&trialLength=${trialLength}`;
		if (supportsAppStoreRedirect) {
			redirect(redeemUrl);
		}
	});

	const getCurrentState = () => {
		if (isLoading) {
			return 'loading';
		}
		if (user) {
			if (canRedeem) {
				return 'redeem';
			}
			if (user?.subscription?.valid) {
				return 'already-subscribed';
			}
			if (user?.subscription?.has_ever_done_free_trial) {
				return 'already-free-trialed';
			}
			return 'invalid-code';
		}
		if (!canRedeem && !!code && error) {
			return 'invalid-code';
		}
		return 'login';
	};

	const onLoginClick = (): void => {
		logEvent({ eventName: 'Guest Pass : Redeem : Auth : Clicked' });
		setAuthPrompt('signup');
		if (setIsModalOpen) {
			setIsModalOpen(true);
		}
	};

	const state = getCurrentState();

	useEffect(() => {
		if (typeof code === 'string') {
			logEvent({
				eventName: 'Guest Pass : Redeem : Landed',
				eventProps: {
					guest_pass_code: code,
					user_state: state,
				},
			});
		}
	}, [code, state, logEvent]);

	useEffect(() => {
		if (state === 'invalid-code') {
			logEvent({
				eventName: 'Guest Pass : Redeem : Fail',
				eventProps: {
					reason: 'invalid code',
				},
			});
		}
	}, [state, logEvent]);

	const redeemGift = () => {
		const supportsAppStoreRedirect = deviceSupportsAppStoreRedirect(window?.navigator?.userAgent);
		const isOfferCode = code && code.length === 18;
		const redeemUrl =
			supportsAppStoreRedirect && isOfferCode
				? `${appStoreURL}${code}` /// https://apps.apple.com/redeem?ctx=offercodes&id=571800810&code=${code}
				: `/guest-pass/subscribe?guestPass=${code}&trialLength=${trialLength}`;

		if (code) {
			logEvent({
				eventName: 'Guest Pass : Redeem : Begin',
				eventProps: {
					guest_pass_code: code,
					user_state: state,
					redeem_url: redeemUrl,
					device_supports_app_store_redirect: supportsAppStoreRedirect,
				},
			});
		}

		redirect(redeemUrl);
	};

	const currentFormContent = () => {
		const loginUi = (
			<>
				<GuestPassTitle>{formatMessage(messages.createOrLogin)}</GuestPassTitle>
				<GuestPassText>{adoptOrphans(formatMessage(messages.createAccount))}</GuestPassText>
				<Button fullWidth backgroundColor="success" onPress={onLoginClick}>
					{formatMessage(messages.continue)}
				</Button>
			</>
		);
		switch (state) {
			case 'loading':
				return <Loader />;
			case 'login':
				return loginUi;
			case 'redeem':
				return (
					<>
						<GuestPassTitle>{formatMessage(messages.guestPassTitle)}</GuestPassTitle>
						<GuestPassText>{adoptOrphans(formatMessage(messages.loggedIn))}</GuestPassText>
						<Button fullWidth backgroundColor="success" onPress={redeemGift}>
							{formatMessage(messages.redeemCopy)}
						</Button>
						<TermsText>
							{formatMessage(messages.terms3, {
								terms: <Link href="/terms">{formatMessage(messages.terms)}</Link>,
								privacyPolicy: <Link href="/privacy">{formatMessage(messages.privacyPolicy)}</Link>,
							})}
						</TermsText>
					</>
				);
			case 'already-subscribed':
				return (
					<>
						<GuestPassTitle>{formatMessage(messages.alreadySubscribedTitle)}</GuestPassTitle>
						<GuestPassText>
							{formatMessage(messages.alreadySubscribedSubtitle1)}{' '}
							{formatMessage(messages.alreadySubscribedSubtitle2, {
								trialLength,
							})}
						</GuestPassText>
						<Button fullWidth backgroundColor="success" onPress={() => redirect('/meditate')}>
							{formatMessage(messages.alreadySubscribedCta)}
						</Button>
					</>
				);
			case 'already-free-trialed':
				return (
					<>
						<GuestPassTitle>{formatMessage(messages.alreadyFreeTrialedTitle)}</GuestPassTitle>
						<GuestPassText>{adoptOrphans(formatMessage(messages.alreadyFreeTrialedSubtitle1))}</GuestPassText>
						<Button fullWidth backgroundColor="success" onPress={() => redirect('/subscribe')}>
							{formatMessage(messages.alreadyFreeTrialedCta)}
						</Button>
					</>
				);
			case 'invalid-code':
				return (
					<>
						<GuestPassTitle>{formatMessage(messages.invalidCode)}</GuestPassTitle>
						<GuestPassText>{adoptOrphans(formatMessage(messages.invalidCodeSubtitle))}</GuestPassText>
					</>
				);
			default:
				return loginUi;
		}
	};

	return (
		<Container>
			<FullscreenBackground src={background} objectFit="cover" />
			<Overlay />
			<Wrapper>
				<RedeemItemsWrapper id={MAIN_CONTENT_ID}>
					<RedeemColumn mobileOrder={2}>{currentFormContent()}</RedeemColumn>
					<GuestPassImageColumn mobileOrder={1}>
						<ClaimYourPassText>
							{state !== 'invalid-code' && formatMessage(messages.title, { sender, trialLength })}
						</ClaimYourPassText>
						<GuestPassImage>
							<Image src={guestPassImage} alt="Guest Pass" style={{ width: '100%', height: 'auto' }} />
						</GuestPassImage>
					</GuestPassImageColumn>
				</RedeemItemsWrapper>
			</Wrapper>
		</Container>
	);
};

export default GuestPassRedeem;
